/**
 * App entrypoint — Router + global providers + route table.
 *
 * Day 15 Sprint 1D completion (this revision):
 *   - Added /accept-invite (public, no auth) for the user invitation flow
 *   - Added /loyalty (admin) for the loyalty program settings page
 *   - TrialBanner mounted inside AuthedLayout so every authed page shows it
 *
 * Jun 17:
 *   - Added /admin/shifts (admin) → ShiftsList. The Sidebar "Day close" link
 *     pointed here but only /admin/shifts/:shiftId (ZReportViewer) was routed,
 *     so the list 404'd. This is the parent list page.
 *
 * Pre-existing structure (preserved verbatim):
 *   - /r/:token (public receipt view) is BEFORE the * catch-all
 *   - SubscriptionProvider was removed in Day 14.5 (still removed)
 *   - DisplayCurrencyProvider, OfflineProvider, AuthProvider stack unchanged
 */
import React, { lazy, Suspense } from "react";
import useBusinessStore from "@/stores/businessStore";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { businessScopedHash } from "@/lib/query-scope";
import { ThemeProvider } from "./components/ui/theme-provider";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import { AuthProvider, useAuth } from "./context/AuthContext";
import {
  RequireAuth,
  RequireAdmin,
  NoAuth,
  RequireTenantOwner,
} from "./components/auth/RouteGuards";

import { PageLayout } from "./components/layout/PageLayout";
import CashierBottomNav from "./components/cashier/CashierBottomNav";

import Index from "./pages/Index";
const BookDemo = lazy(() => import("./pages/BookDemo"));
const Contact = lazy(() => import("./pages/Contact"));
const EnterpriseSales = lazy(() => import("./pages/EnterpriseSales"));
const EnterpriseQuote = lazy(() => import("./pages/EnterpriseQuote"));
const PayLink = lazy(() => import("./pages/PayLink"));
const QuoteView = lazy(() => import("./pages/public/QuoteView"));
const StatementView = lazy(() => import("./pages/public/StatementView"));
const ProjectPortal = lazy(() => import("./pages/public/ProjectPortal"));
const PortalInvite = lazy(() => import("./pages/public/PortalInvite"));
const PortalHome = lazy(() => import("./pages/public/PortalHome"));
const PortalLogin = lazy(() => import("./pages/public/PortalHome").then((m) => ({ default: m.PortalLogin })));
const CustomerStatement = lazy(() => import("./pages/admin/CustomerStatement"));
const ReorderSuggestions = lazy(() => import("./pages/admin/ReorderSuggestions"));
const DispatchList = lazy(() => import("./pages/admin/Dispatches").then((m) => ({ default: m.DispatchList })));
const NewDispatch = lazy(() => import("./pages/admin/Dispatches").then((m) => ({ default: m.NewDispatch })));
const DispatchDetail = lazy(() => import("./pages/admin/Dispatches").then((m) => ({ default: m.DispatchDetail })));
const DistributionSetup = lazy(() => import("./pages/admin/DistributionSetup"));
const TerritoryReportPage = lazy(() => import("./pages/admin/DistributionSetup").then((m) => ({ default: m.TerritoryReportPage })));
const MyDeliveries = lazy(() => import("./pages/driver/MyDeliveries"));
const VanList = lazy(() => import("./pages/admin/Vans").then((m) => ({ default: m.VanList })));
const NewVan = lazy(() => import("./pages/admin/Vans").then((m) => ({ default: m.NewVan })));
const VanDetail = lazy(() => import("./pages/admin/Vans").then((m) => ({ default: m.VanDetail })));
const MyVan = lazy(() => import("./pages/driver/MyVan"));
const LiveRuns = lazy(() => import("./pages/admin/LiveRuns"));
const TwoStepSetupPage = lazy(() => import("./pages/auth/TwoStepSetupPage"));
const DriversPage = lazy(() => import("./pages/admin/Drivers"));
const RunHistory = lazy(() => import("./pages/admin/RunHistory"));
const PriceLists = lazy(() => import("./pages/admin/PriceLists"));
const SalesDocsPage = lazy(() => import("./pages/admin/SalesDocs"));
const SalesDocEditorPage = lazy(() => import("./pages/admin/SalesDocs").then((m) => ({ default: m.SalesDocEditorPage })));
const SalesDocViewPage = lazy(() => import("./pages/admin/SalesDocs").then((m) => ({ default: m.SalesDocViewPage })));
import PageBusinessScope from "@/components/group/PageBusinessScope";
const EnterpriseWelcome = lazy(() => import("./pages/EnterpriseWelcome"));
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/layout/ScrollToTop";

const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const PaymentVerification = lazy(
  () => import("./pages/auth/PaymentVerification"),
);
const PortfolioOverview = lazy(() => import("./pages/auth/PortfolioOverview"));
const Products = lazy(() => import("./pages/admin/Products"));

const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const Report = lazy(() => import("./pages/admin/Report"));
const ReportCenter = lazy(() => import("./pages/admin/ReportCenter"));
const ProjectsReport = lazy(() => import("./pages/admin/ProjectsReport"));
const Statements = lazy(() => import("./pages/admin/Statements"));
const Employees = lazy(() => import("./pages/admin/hr/Employees"));
const EmployeeProfile = lazy(() => import("./pages/admin/hr/EmployeeProfile"));
const LeavePage = lazy(() => import("./pages/admin/hr/Leave"));
const PayrollRuns = lazy(() => import("./pages/admin/hr/PayrollRuns"));
const PayrollRunPage = lazy(() => import("./pages/admin/hr/PayrollRun"));
const MyHr = lazy(() => import("./pages/MyHr"));
const LowStock = lazy(() => import("./pages/admin/LowStock"));
const ReceiveStock = lazy(() => import("./pages/admin/ReceiveStock"));
const Returns = lazy(() => import("./pages/admin/Returns"));
const Labels = lazy(() => import("./pages/admin/Labels"));
const Promotions = lazy(() => import("./pages/admin/Promotions"));
const StockTake = lazy(() => import("./pages/admin/StockTake"));
const StockCountPage = lazy(() => import("./pages/StockCount"));
const Approval = lazy(() => import("./pages/Approval"));
const MyReturns = lazy(() => import("./pages/cashier/MyReturns"));
const MyShift = lazy(() => import("./pages/cashier/MyShift"));
const WhatsNew = lazy(() => import("./pages/WhatsNew"));
const Help = lazy(() => import("./pages/Help"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const ActionHistory = lazy(() => import("./pages/admin/ActionHistory"));
const TeamChat = lazy(() => import("./pages/admin/TeamChat"));
const ExpenditureManagement = lazy(() => import("./pages/admin/Expenditure"));

const Subscriptions = lazy(() => import("./pages/admin/Subscriptions"));
const Notifications = lazy(() => import("./pages/admin/Notifications"));

// ── Sprint 1D additions ────────────────────────────────────────────
const AcceptInvite = lazy(() => import("./pages/public/AcceptInvite"));
const Unsubscribe = lazy(() => import("./pages/public/Unsubscribe"));
const Crm = lazy(() => import("./pages/admin/Crm"));
const LoyaltyProgramSettings = lazy(
  () => import("./pages/admin/LoyaltyProgramSettings"),
);
// ───────────────────────────────────────────────────────────────────

import Connections from "@/pages/admin/Connections";
import GroupView from "@/pages/GroupView";
import { DisplayCurrencyProvider } from "@/context/DisplayCurrencyContext";

const CashierDashboard = lazy(() => import("./pages/cashier/CashierDashboard"));
const SalesHistory = lazy(() => import("./pages/cashier/SalesHistory"));
const CashierAccount = lazy(() => import("./pages/cashier/CashierAccount"));
const Inventory = lazy(() => import("./pages/cashier/Inventory"));
const Expenditure = lazy(() => import("./pages/cashier/Expenditure"));
const Chat = lazy(() => import("./pages/cashier/Chat"));

const PaymentCallbackHandler = lazy(
  () => import("./components/subscription/PaymentCallbackHandler"),
);

const TeamPage = lazy(() => import("./pages/admin/Team"));
import GroupApprovalsPage from "@/pages/group/Approvals";
import GroupCatalogPage from "@/pages/group/Catalog";
const GroupTransfersPage = lazy(() => import("./pages/group/Transfers"));
const ZReportViewer = lazy(() => import("./components/shifts/ZReportViewer"));
const ShiftsList = lazy(() => import("./pages/admin/ShiftsList"));
import useEnsureActiveBusiness, { useSyncBusinessList } from "@/hooks/useEnsureActiveBusiness";
import AuditLog from "@/pages/admin/AuditLog";
import { OfflineProvider } from "@/context/OfflineProvider";
import api from "@/services/api";
import SyncStatusBar from "@/components/offline/SyncStatusBar";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import ReceiptView from "@/pages/public/ReceiptView";

// Sprint 1D — TrialBanner mounted on every authed page
import { TrialBanner } from "@/components/common/TrialBanner";

import "./App.css";
import SubscriptionStatusAlert from "./components/subscription/SubscriptionStatusAlert";
import EnterpriseGate from "./components/enterprise/EnterpriseGate";
import PlanLimitPrompt from "./components/subscription/PlanLimitPrompt";
import ChatPresence from "./components/chat/ChatPresence";
const ScheduledOrders = lazy(() => import("./pages/cashier/ScheduledOrders"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const CheckInDesk = lazy(() => import("./pages/cashier/CheckInDesk"));
const Stylists = lazy(() => import("./pages/admin/Stylists"));
const Classes = lazy(() => import("./pages/admin/Classes"));
const Messaging = lazy(() => import("./pages/admin/Messaging"));
import RenewPage from "@/pages/RenewPage";
import LocationsPage from "./pages/settings/LocationsPage";
const Projects = lazy(() => import("./pages/admin/Projects"));
const ProjectDetail = lazy(() => import("./pages/admin/ProjectDetail"));
const CashierCollect = lazy(() => import("./pages/cashier/CashierCollect"));
const AdminCollections = lazy(() => import("./pages/admin/AdminCollections"));
const BranchesPage = lazy(() => import("./pages/admin/BranchesPage"));
const TransfersPage = lazy(() => import("./pages/admin/TransfersPage"));
const ReceivablesPage = lazy(() => import("./pages/admin/ReceivablesPage"));
const PurchasingPage = lazy(() => import("./pages/admin/PurchasingPage"));
const BatchesPage = lazy(() => import("./pages/admin/BatchesPage"));
const BranchScorecardPage = lazy(() => import("./pages/admin/BranchScorecardPage"));
const GroupBranchesPage = lazy(() => import("./pages/admin/BranchScorecardPage").then((m) => ({ default: m.GroupBranchesPage })));
const ExceptionsPage = lazy(() => import("./pages/admin/BranchScorecardPage").then((m) => ({ default: m.ExceptionsPage })));
const ExportPage = lazy(() => import("./pages/admin/BranchScorecardPage").then((m) => ({ default: m.ExportPage })));
const RecallsPage = lazy(() => import("./pages/admin/RecallsPage"));
const DispensingPage = lazy(() => import("./pages/admin/DispensingPage"));
const JobsPage = lazy(() => import("./pages/admin/JobsPage"));
const WeighbridgePage = lazy(() => import("./pages/admin/WeighbridgePage"));
const WeighTicketPrintPage = lazy(() => import("./pages/admin/WeighTicketPrintPage"));
const JobDetailPage = lazy(() => import("./pages/admin/JobDetailPage"));
const JobBoardPage = lazy(() => import("./pages/admin/JobBoardPage"));
const JobApprovalsPage = lazy(() => import("./pages/admin/JobApprovalsPage"));
const ServiceContractsPage = lazy(() => import("./pages/admin/ServiceContractsPage"));
const ServiceContractPage = lazy(() => import("./pages/admin/ServiceContractsPage").then((m) => ({ default: m.ServiceContractPage })));
const PatientsPage = lazy(() => import("./pages/admin/PatientsPage"));
const ControlledRegisterPage = lazy(() => import("./pages/admin/ControlledRegisterPage"));
const RecallDetailPage = lazy(() => import("./pages/admin/RecallsPage").then((m) => ({ default: m.RecallDetailPage })));
const ManufacturingPage = lazy(() => import("./pages/admin/ManufacturingPage"));
const StockExpiryPage = lazy(() => import("./pages/admin/StockExpiryPage"));
const JournalsPage = lazy(() => import("./pages/admin/books/Journals"));
const BankReconciliationPage = lazy(() => import("./pages/admin/books/BankReconciliation"));
const FixedAssetsPage = lazy(() => import("./pages/admin/books/FixedAssets"));
const TaxRatesPage = lazy(() => import("./pages/admin/books/TaxRates"));


const BrandedLoadingState = ({ label = "Loading..." }: { label?: string }) => (
  <div className="flex h-screen w-full items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  </div>
);

interface AuthedLayoutProps {
  children: React.ReactNode;
  title: string;
  hideHeader?: boolean;
  noPadding?: boolean;
}

// TrialBanner mounted ABOVE PageLayout so it spans full width. Banner
// self-hides for non-tenant_owner roles and inactive trial states.
const AuthedLayout = ({
  children,
  title,
  hideHeader,
  noPadding,
}: AuthedLayoutProps) => (
  <>
    <EnterpriseGate />
    <TrialBanner />
    <SubscriptionStatusAlert />
    <PlanLimitPrompt />
    <PageLayout title={title} hideHeader={hideHeader} noPadding={noPadding}>
      <PageBusinessScope>{children}</PageBusinessScope>
      <ChatPresence />
      <SyncStatusBar />
    </PageLayout>
  </>
);

const AppRoutes = () => {
  return (
    <>
      {/* Apple-style: frosted cards that stack and fan out on hover. */}
      <Toaster
        position="top-right"
        closeButton
        expand={false}
        visibleToasts={4}
        gap={10}
        offset={16}
        toastOptions={{
          className:
            "!rounded-2xl !border !border-white/50 !bg-white/80 !text-foreground !shadow-[0_8px_30px_rgba(0,0,0,0.12)] !backdrop-blur-xl dark:!border-white/10 dark:!bg-zinc-900/80",
        }}
      />

      <Suspense fallback={<BrandedLoadingState />}>
        <Routes>
          {/* ── Public landing + marketing ── */}
          <Route path="/" element={<Index />} />
          <Route path="/book-demo" element={<BookDemo />} />
          <Route path="/contact-sales" element={<Contact />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/enterprise" element={<EnterpriseSales />} />
          <Route path="/enterprise/quote/:token" element={<EnterpriseQuote />} />
          <Route path="/pay/:code" element={<PayLink />} />
          <Route path="/q/:token" element={<QuoteView />} />
          <Route path="/s/:token" element={<StatementView />} />
          {/* Project pages for clients / owners / investors / buyers — own accounts */}
          <Route path="/p/:token" element={<PortalInvite />} />
          <Route path="/portal" element={<PortalHome />} />
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/projects/:accessId" element={<ProjectPortal />} />

          {/* ── Public token-based pages (unauth) — BEFORE catch-all ── */}
          <Route path="/r/:token" element={<ReceiptView />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />

          {/* ── Auth routes ── */}
          <Route
            path="/login"
            element={
              <NoAuth>
                <Login />
              </NoAuth>
            }
          />
          <Route path="/security/two-step" element={<TwoStepSetupPage />} />
          <Route
            path="/register"
            element={
              <NoAuth>
                <Register />
              </NoAuth>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <NoAuth>
                <ForgotPassword />
              </NoAuth>
            }
          />
          <Route
            path="/reset-password"
            element={
              <NoAuth>
                <ResetPassword />
              </NoAuth>
            }
          />

          {/* ── Portfolio + Group (tenant_owner) ── */}
          <Route
            path="/portfolio"
            element={
              <RequireTenantOwner>
                <AuthedLayout title="Portfolio" hideHeader noPadding>
                  <PortfolioOverview />
                </AuthedLayout>
              </RequireTenantOwner>
            }
          />
          {/* Group → Users moved into Team & access. */}
          <Route path="/group/users" element={<Navigate to="/team" replace />} />
          <Route
            path="/group/approvals"
            element={
              <RequireAuth permission="group.approval.read">
                <AuthedLayout title="Group" hideHeader noPadding>
                  <GroupApprovalsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/group/catalog"
            element={
              <RequireAuth permission="group.product.read">
                <AuthedLayout title="Group" hideHeader noPadding>
                  <GroupCatalogPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/group/transfers"
            element={
              <RequireAdmin permission="group.transfer.read">
                <AuthedLayout title="Group transfers" hideHeader noPadding>
                  <GroupTransfersPage />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/group"
            element={
              <RequireAuth permission="report.group.read">
                <AuthedLayout title="Group view">
                  <GroupView />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          {/* ── Payment callbacks ── */}
          <Route
            path="/payment-verification"
            element={<PaymentVerification />}
          />
          <Route
            path="/whats-new"
            element={
              <RequireAuth>
                <AuthedLayout title="What's new">
                  <WhatsNew />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/help/:section?"
            element={
              <RequireAuth>
                <AuthedLayout title="Help">
                  <Help />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/payment-callback"
            element={
              <RequireAuth>
                <PaymentCallbackHandler />
              </RequireAuth>
            }
          />

          {/* ── Admin (tenant_owner + business_admin) ── */}
          <Route
            path="/welcome"
            element={
              <RequireAuth>
                <EnterpriseWelcome />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireAdmin permission="report.dashboard.read">
                <AuthedLayout title="Dashboard">
                  <Dashboard />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/products"
            element={
              <RequireAdmin permission="product.read">
                <AuthedLayout title="Products">
                  <Products />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/reports"
            element={
              <RequireAdmin permission={["report.pnl.read",  "report.sales.read",  "report.cash-flow.read",  "report.trial-balance.read",  "report.tax.read"]}>
                <AuthedLayout title="Report Center">
                  <ReportCenter />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/crm"
            element={
              <RequireAdmin permission={["crm.follow-up.read",  "crm.template.read"]}>
                <AuthedLayout title="Customer relationships">
                  <Crm />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/crm/follow-ups"
            element={
              <RequireAuth>
                <AuthedLayout title="Follow-ups">
                  <Crm />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/hr/employees"
            element={
              <RequireAdmin permission="employee.read">
                <AuthedLayout title="Employees">
                  <Employees />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/hr/employees/:id"
            element={
              <RequireAdmin permission="employee.read">
                <AuthedLayout title="Employee">
                  <EmployeeProfile />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/hr/leave"
            element={
              <RequireAuth>
                <AuthedLayout title="Leave">
                  <LeavePage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll"
            element={
              <RequireAdmin permission="payroll.run.read">
                <AuthedLayout title="Payroll">
                  <PayrollRuns />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/payroll/:id"
            element={
              <RequireAdmin permission="payroll.run.read">
                <AuthedLayout title="Payroll">
                  <PayrollRunPage />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/my/leave"
            element={
              <RequireAuth>
                <AuthedLayout title="My leave & pay">
                  <MyHr />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/my-hr"
            element={
              <RequireAuth till>
                <AuthedLayout title="My leave & pay">
                  <MyHr />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/reports/projects"
            element={
              <RequireAdmin>
                <AuthedLayout title="Project report">
                  <ProjectsReport />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/reports/statements"
            element={
              <RequireAdmin>
                <AuthedLayout title="Statements">
                  <Statements />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/report"
            element={
              <RequireAdmin>
                <AuthedLayout title="Reports">
                  <Report />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/approvals/:id"
            element={
              <RequireAuth>
                <AuthedLayout title="Approve a return">
                  <Approval />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/labels"
            element={
              <RequireAdmin permission="product.read">
                <AuthedLayout title="Shelf labels">
                  <Labels />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/stock-take"
            element={
              <RequireAdmin permission="stock-count.read">
                <AuthedLayout title="Stock take">
                  <StockTake />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/cashier/stock-take"
            element={
              <RequireAuth till>
                <AuthedLayout title="Stock count">
                  <StockTake />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/stock-take/:id"
            element={
              <RequireAuth>
                <AuthedLayout title="Stock count">
                  <StockCountPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/promotions"
            element={
              <RequireAdmin permission="product.tier-price.update">
                <AuthedLayout title="Promotions">
                  <Promotions />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/returns"
            element={
              <RequireAdmin permission="order.read">
                <AuthedLayout title="Returns">
                  <Returns />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/receive-stock"
            element={
              <RequireAdmin permission="product.stock.add">
                <AuthedLayout title="Receive stock">
                  <ReceiveStock />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/low-stock"
            element={
              <RequireAdmin permission="product.read">
                <AuthedLayout title="Low Stock">
                  <LowStock />
                </AuthedLayout>
              </RequireAdmin>
            }
          />

          <Route
            path="/orders"
            element={
              <RequireAdmin permission="order.read">
                <AuthedLayout title="Orders">
                  <Orders />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/action-history"
            element={
              <RequireAdmin permission={["activity.read.all",  "activity.read.own"]}>
                <AuthedLayout title="Action History">
                  <ActionHistory />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/expenditures"
            element={
              <RequireAdmin permission="expenditure.read">
                <AuthedLayout title="Expenditures">
                  <ExpenditureManagement />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/audit-log"
            element={
              <RequireAdmin permission="audit-log.read">
                <AuthedLayout title="Audit log">
                  <AuditLog />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/connections"
            element={
              <RequireAdmin permission="connection.read">
                <AuthedLayout title="Connections">
                  <Connections />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/connections/handshakes/:handshakeId"
            element={
              <RequireAdmin permission="connection.read">
                <AuthedLayout title="Connections">
                  <Connections />
                </AuthedLayout>
              </RequireAdmin>
            }
          />

          <Route
            path="/connections/announcements/:announcementId"
            element={
              <RequireAdmin permission="connection.read">
                <AuthedLayout title="Connections">
                  <Connections />
                </AuthedLayout>
              </RequireAdmin>
            }
          />

          <Route
            path="/manufacturing"
            element={
              <RequireAuth permission={["bom.read",  "production-order.read"]}>
                <AuthedLayout title="Manufacturing">
                  <ManufacturingPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route path="/books/journals" element={<RequireAuth permission="journal.manual.read"><AuthedLayout title="Journals"><JournalsPage /></AuthedLayout></RequireAuth>} />
          <Route path="/books/bank" element={<RequireAuth permission="bank.reconcile.read"><AuthedLayout title="Bank reconciliation"><BankReconciliationPage /></AuthedLayout></RequireAuth>} />
          <Route path="/books/assets" element={<RequireAuth permission="fixed-asset.read"><AuthedLayout title="Fixed assets"><FixedAssetsPage /></AuthedLayout></RequireAuth>} />
          <Route path="/books/tax-rates" element={<RequireAuth permission="tax.manage"><AuthedLayout title="Tax rates"><TaxRatesPage /></AuthedLayout></RequireAuth>} />
          <Route
            path="/stock-expiry"
            element={
              <RequireAuth permission="stock-expiry.report.read">
                <AuthedLayout title="Stock expiry">
                  <StockExpiryPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/admin/shifts"
            element={
              <RequireAdmin permission="shift.read.all">
                <AuthedLayout title="Shifts">
                  <ShiftsList />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/shifts/:shiftId"
            element={
              <RequireAdmin permission={["shift.read.all",  "shift.z-report.read"]}>
                <AuthedLayout title="Z-report">
                  <ZReportViewer />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <RequireTenantOwner>
                <AuthedLayout title="Subscription">
                  <Subscriptions />
                </AuthedLayout>
              </RequireTenantOwner>
            }
          />
          <Route
            path="/stylists"
            element={
              <RequireAdmin permission="staff.read">
                <AuthedLayout title="Stylists">
                  <Stylists />
                </AuthedLayout>
              </RequireAdmin>
            }
          />

          <Route
            path="/classes"
            element={
              <RequireAdmin permission="class.read">
                <AuthedLayout title="Classes">
                  <Classes />
                </AuthedLayout>
              </RequireAdmin>
            }
          />

          <Route
            path="/projects"
            element={
              <RequireAdmin permission="project.read">
                <AuthedLayout title="Projects">
                  <Projects />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <RequireAdmin permission="project.read">
                <AuthedLayout title="Project">
                  <ProjectDetail />
                </AuthedLayout>
              </RequireAdmin>
            }
          />

          <Route
            path="/collections"
            element={
              <RequireAuth permission="project.manage">
                <AuthedLayout title="Collections">
                  <AdminCollections />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/stock-transfers"
            element={
              <RequireAuth permission="location.read">
                <AuthedLayout title="Stock transfers">
                  <TransfersPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/receivables"
            element={
              <RequireAuth permission="credit.ageing.read">
                <AuthedLayout title="Receivables">
                  <ReceivablesPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/customers/:id/statement"
            element={
              <RequireAuth permission="credit.statement.read">
                <AuthedLayout title="Statement">
                  <CustomerStatement />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/dispatch"
            element={
              <RequireAuth permission="dispatch.read">
                <AuthedLayout title="Dispatch">
                  <DispatchList />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/dispatch/new"
            element={
              <RequireAuth permission="dispatch.manage">
                <AuthedLayout title="New dispatch">
                  <NewDispatch />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/dispatch/:id"
            element={
              <RequireAuth permission="dispatch.read">
                <AuthedLayout title="Dispatch">
                  <DispatchDetail />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/distribution/routes"
            element={
              <RequireAuth permission="route.read">
                <AuthedLayout title="Routes">
                  <DistributionSetup />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/distribution/drivers"
            element={
              <RequireAuth permission={["driver.manage",  "dispatch.read",  "van.read"]}>
                <AuthedLayout title="Drivers">
                  <DriversPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/distribution/history"
            element={
              <RequireAuth permission={["dispatch.read",  "van.read"]}>
                <AuthedLayout title="Run history">
                  <RunHistory />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/my/runs"
            element={
              <RequireAuth permission={["dispatch.deliver",  "van.sell"]}>
                <AuthedLayout title="My runs">
                  <RunHistory mine />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/distribution/live"
            element={
              <RequireAuth permission={["dispatch.read",  "van.read"]}>
                <AuthedLayout title="Live map">
                  <LiveRuns />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/distribution/report"
            element={
              <RequireAuth permission="route.read">
                <AuthedLayout title="Sales by territory">
                  <TerritoryReportPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/deliveries"
            element={
              <RequireAuth permission="dispatch.deliver">
                <AuthedLayout title="My deliveries">
                  <MyDeliveries />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/deliveries/:id"
            element={
              <RequireAuth permission="dispatch.deliver">
                <AuthedLayout title="My deliveries">
                  <MyDeliveries />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/vans"
            element={
              <RequireAuth permission="van.read">
                <AuthedLayout title="Vans">
                  <VanList />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/vans/new"
            element={
              <RequireAuth permission="van.read">
                <AuthedLayout title="Load a van">
                  <NewVan />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/vans/:id"
            element={
              <RequireAuth permission="van.read">
                <AuthedLayout title="Van run">
                  <VanDetail />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/van"
            element={
              <RequireAuth permission="van.sell">
                <AuthedLayout title="My van">
                  <MyVan />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/reorder"
            element={
              <RequireAuth permission="purchase-order.create">
                <AuthedLayout title="Reorder">
                  <ReorderSuggestions />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/price-lists"
            element={
              <RequireAuth permission="price-list.read">
                <AuthedLayout title="Price lists">
                  <PriceLists />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/quotations"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="Quotations">
                  <SalesDocsPage kind="quote" />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/quotations/new"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="New quotation">
                  <SalesDocEditorPage kind="quote" />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/quotations/:id"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="Quotation">
                  <SalesDocViewPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/quotations/:id/edit"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="Edit quotation">
                  <SalesDocEditorPage kind="quote" />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/sales-orders"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="Sales orders">
                  <SalesDocsPage kind="order" />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/sales-orders/new"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="New sales order">
                  <SalesDocEditorPage kind="order" />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/sales-orders/:id"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="Sales order">
                  <SalesDocViewPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/sales-orders/:id/edit"
            element={
              <RequireAuth permission="sales-order.read">
                <AuthedLayout title="Edit sales order">
                  <SalesDocEditorPage kind="order" />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/branch-scorecard"
            element={
              <RequireAuth permission={["report.branch.read", "report.branch.read.own"]}>
                <AuthedLayout title="Branches">
                  <BranchScorecardPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/exceptions"
            element={
              <RequireAuth permission={["exception.read", "exception.manage"]}>
                <AuthedLayout title="Exceptions">
                  <ExceptionsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/group/branches"
            element={
              <RequireAuth permission="report.group.read">
                <AuthedLayout title="Branches across the group">
                  <GroupBranchesPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/exports/:id"
            element={
              <RequireAuth>
                <AuthedLayout title="Export">
                  <ExportPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/batches"
            element={
              <RequireAuth permission="batch.read">
                <AuthedLayout title="Batches">
                  <BatchesPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/weighbridge"
            element={
              <RequireAuth permission={["weighbridge.read", "weighbridge.weigh", "weighbridge.manage"]}>
                <AuthedLayout title="Weighbridge">
                  <WeighbridgePage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/weighbridge/:id/print"
            element={
              <RequireAuth permission={["weighbridge.read", "weighbridge.weigh", "weighbridge.manage"]}>
                <WeighTicketPrintPage />
              </RequireAuth>
            }
          />
          <Route
            path="/jobs"
            element={
              <RequireAuth permission={["job.read", "job.read.own", "job.manage"]}>
                <AuthedLayout title="Jobs">
                  <JobsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/jobs/board"
            element={
              <RequireAuth permission={["job.read", "job.read.own", "job.manage"]}>
                <AuthedLayout title="Board">
                  <JobBoardPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/jobs/approvals"
            element={
              <RequireAuth permission="job.approve">
                <AuthedLayout title="Time & expenses">
                  <JobApprovalsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <RequireAuth permission={["job.read", "job.read.own", "job.manage"]}>
                <AuthedLayout title="Job">
                  <JobDetailPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/service-contracts"
            element={
              <RequireAuth permission={["contract.manage", "job.read"]}>
                <AuthedLayout title="Service contracts">
                  <ServiceContractsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/service-contracts/:id"
            element={
              <RequireAuth permission={["contract.manage", "job.read"]}>
                <AuthedLayout title="Service contract">
                  <ServiceContractPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/dispensing"
            element={
              <RequireAuth permission="dispense.read">
                <AuthedLayout title="Dispensing">
                  <DispensingPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/patients"
            element={
              <RequireAuth permission="patient.read">
                <AuthedLayout title="Patients">
                  <PatientsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/controlled-register"
            element={
              <RequireAuth permission="controlled.read">
                <AuthedLayout title="Controlled register">
                  <ControlledRegisterPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/recalls"
            element={
              <RequireAuth permission="batch.read">
                <AuthedLayout title="Recalls">
                  <RecallsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/recalls/:id"
            element={
              <RequireAuth permission="batch.read">
                <AuthedLayout title="Recall">
                  <RecallDetailPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/purchasing"
            element={
              <RequireAuth permission="purchase-order.read">
                <AuthedLayout title="Purchasing">
                  <PurchasingPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          {/* ── Sprint 1D Loyalty (admin) ── */}
          <Route
            path="/loyalty"
            element={
              <RequireAdmin permission="loyalty.program.read">
                <AuthedLayout title="Loyalty">
                  <LoyaltyProgramSettings />
                </AuthedLayout>
              </RequireAdmin>
            }
          />
          <Route
            path="/customers"
            element={
              <RequireAuth>
                <AuthedLayout title="Customers">
                  <Customers />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/settings/locations"
            element={
              <RequireAuth permission="location.read.tree">
                <AuthedLayout title="Locations">
                  <LocationsPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/branches"
            element={
              <RequireAuth permission="branch.overview.read">
                <AuthedLayout title="Branches">
                  <BranchesPage />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/messaging"
            element={
              <RequireAdmin permission="campaign.read">
                <AuthedLayout title="Messaging">
                  <Messaging />
                </AuthedLayout>
              </RequireAdmin>
            }
          />

          {/* ── Any-authed routes ── */}
          {/* Team & access: the page itself says if you can't manage anyone (checks are per place). */}
          {["/team", "/team/:userId"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAuth>
                  <AuthedLayout title="Team & access">
                    <TeamPage />
                  </AuthedLayout>
                </RequireAuth>
              }
            />
          ))}
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <AuthedLayout title="Settings">
                  <Settings />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/team-chat"
            element={
              <RequireAuth>
                <AuthedLayout title="Team Chat">
                  <TeamChat />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <AuthedLayout title="Notifications">
                  <Notifications />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          {/* ── Cashier routes ── */}
          <Route
            path="/cashier"
            element={
              <RequireAuth till>
                <AuthedLayout title="Point of Sale">
                  <CashierDashboard />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/returns"
            element={
              <RequireAuth till>
                <AuthedLayout title="My returns">
                  <MyReturns />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/sales"
            element={
              <RequireAuth till>
                <AuthedLayout title="Sales History">
                  <SalesHistory />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/inventory"
            element={
              <RequireAuth till>
                <AuthedLayout title="Inventory Check">
                  <Inventory />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/schedule"
            element={
              <RequireAuth till>
                <AuthedLayout title="Schedule">
                  <ScheduledOrders />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/cashier/checkin"
            element={
              <RequireAuth till>
                <AuthedLayout title="Check-in">
                  <CheckInDesk />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/cashier/collect"
            element={
              <RequireAuth till>
                <AuthedLayout title="Collect Payment">
                  <CashierCollect />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/cashier/expenditure"
            element={
              <RequireAuth till>
                <AuthedLayout title="Expenditure">
                  <Expenditure />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/chat"
            element={
              <RequireAuth till>
                <AuthedLayout title="Team Chat">
                  <Chat />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/account"
            element={
              <RequireAuth till>
                <AuthedLayout title="My Account">
                  <CashierAccount />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/shifts"
            element={
              <RequireAuth till>
                <AuthedLayout title="My shift">
                  <MyShift />
                </AuthedLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/cashier/shifts/:shiftId"
            element={
              <RequireAuth till>
                <AuthedLayout title="Z-report" hideHeader noPadding>
                  <ZReportViewer />
                </AuthedLayout>
              </RequireAuth>
            }
          />

          {/* ── 404 catch-all — MUST be last ── */}
          <Route path="*" element={<NotFound />} />
          <Route path="/renew/:token" element={<RenewPage />} />
        </Routes>
      </Suspense>

      <CashierBottomNav />
    </>
  );
};

const AuthGate = () => {
  const { isLoading: authLoading } = useAuth();
  useEnsureActiveBusiness();
  useSyncBusinessList();
  if (authLoading) return <BrandedLoadingState label="Initializing..." />;
  return <AppRoutes />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Every cached answer belongs to the business it was asked for: the
      // open business, or (in All businesses) the page's own business.
      queryKeyHashFn: businessScopedHash,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Switching business or branch: every cached answer belongs to the one we left, so
// refetch what's on screen now and drop the rest when it's next needed.
useBusinessStore.subscribe((state, prev) => {
  if (state.activeBusinessSlug !== prev.activeBusinessSlug || state.activeBranchByBusiness !== prev.activeBranchByBusiness || state.pageBusinessId !== prev.pageBusinessId) {
    void queryClient.invalidateQueries();
  }
});

/** Error boundary that clears itself when the route changes. */
function RoutedErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="eaze-theme">
      <QueryClientProvider client={queryClient}>
        <Router>
          <ScrollToTop />
          <DisplayCurrencyProvider>
            <OfflineProvider api={api} heartbeatUrl="/health">
              <AuthProvider>
                <RoutedErrorBoundary>
                  <AuthGate />
                </RoutedErrorBoundary>
              </AuthProvider>
            </OfflineProvider>
          </DisplayCurrencyProvider>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
